import { useState, useContext, useEffect, useMemo } from 'react';
import { StylingContext, useAuthContext } from '../../store/Contexts';
import { useNavigate } from 'react-router-dom';
import { useWindowListener } from '../helpers/useWindowListener';
import './Header.css';
import { Avatar, Popover, Box, Divider } from '@mui/material';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import Toast from '../ui/toasts/Toast';
import { Dropdown } from '../helpers/Dropdown';
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import {
    fetchAgencies,
    fetchFavoriteClients,
} from '../../services/systemService';
import LoadingWrapper from '../ui/loaders/LoadingWrapper';
import { logoutSession } from '../../services/authService';
import { NavLink } from 'react-router-dom';
import { LogEventType, UserType } from '../../utils/data/enums';
import {
    getContrastColor,
    handleNavigateToClient,
    handleReturnToClient,
} from '../../utils/utils';
import { logEvent } from '../../services/loggingService';
import { ROUTES } from '../../constants/routes';
import { getText } from '../../i18n';
function Header({ styleOverride = {}, visible = true }) {
    const { showModal } = useContext(ModalContext);
    const {
        isLoggedIn,
        user,
        logout,
        multitenantFlag,
        updateMultitenantFlag,
        setIsLoading,
        adminView,
        setAdminView,
        publicBrand,
        allowAdditionalUser,
    } = useAuthContext();
    const { currentStyling, setCurrentStyling } = useContext(StylingContext);
    const mobileWidth = 550;
    const [navExpanded, setNavExpanded] = useState(
        window.innerWidth > mobileWidth,
    );
    const [favClientList, setFavClientList] = useState([]);
    const navigate = useNavigate();
    const [isFavClientLoading, setIsFavClientLoading] = useState(true);
    const tenant = user?.jwt?.tenant;
    const ind = user?.jwt?.ind;
    const tenantId = window.localStorage.getItem('tenantId');
    const allowViewSwitch = useMemo(
        () => tenant == tenantId,
        [tenant, tenantId],
    );

    useEffect(() => {
        if (isLoggedIn && user?.jwt?.type === UserType.MultiClient) {
            getFavoriteClients();
            return;
        }
        setFavClientList([]);
        setIsFavClientLoading(false);
    }, [isLoggedIn]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 786);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleSetAdminView = admin => {
        setOpen(false);
        setAdminView(admin);
        navigate(ROUTES.ROOT);
        logEvent(
            LogEventType.AdminIndividualViewChanged,
            `User transitioned from ${admin ? 'individual' : 'admin'} to ${admin ? 'admin' : 'individual'} view`,
        );
    };

    const renderViewSwitch = () => {
        if (
            !ind ||
            !allowViewSwitch ||
            multitenantFlag ||
            user?.jwt?.type > UserType.ClientAdmin
        )
            return null;

        return adminView ? (
            <div
                onClick={() => handleSetAdminView(false)}
                className="pointer-cursor profile-link"
            >
                {getText('msg_6aaa1c574bcc')}
            </div>
        ) : (
            <div
                onClick={() => handleSetAdminView(true)}
                className="pointer-cursor profile-link"
            >
                {getText('msg_63baf70131a8')}
            </div>
        );
    };

    const getFavoriteClients = async () => {
        try {
            setIsFavClientLoading(true);
            const data = await fetchFavoriteClients();
            const formattedData = data.map(client => ({
                value: client.tenantId,
                label: client.name,
                companyCode: client.clientCode,
            }));
            formattedData.sort((a, b) => a.label.localeCompare(b.label));
            setFavClientList(formattedData);
        } catch (error) {
            console.error('Could not fetch favorite clients:', error);
        } finally {
            setIsFavClientLoading(false);
        }
    };

    const updateTenant = tenantId => {
        setIsLoading(true);
        let companyCode = favClientList.find(
            client => client.value === tenantId,
        )?.companyCode;
        let clientName = favClientList.find(
            client => client.value === tenantId,
        )?.label;
        if (!clientName) clientName = undefined;
        if (!companyCode) companyCode = undefined;
        handleNavigateToClient(tenantId, companyCode, clientName, false, true);
    };

    const selectedClient = () => {
        if (!multitenantFlag) {
            const tenantId = window.localStorage.getItem('tenantId');
            const clientName = window.localStorage.getItem('clientName');
            if (clientName && clientName !== '') return clientName;
            for (let i = 0; i < favClientList.length; i++) {
                if (favClientList[i].value == tenantId) {
                    return favClientList[i].label;
                }
            }
        } else {
            return 'Select Client';
        }
    };
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 786);

    const handleReturnToClientClick = async () => {
        await handleReturnToClient(user, true);
    };

    useWindowListener('resize', () => {
        let wasExpanded = navExpanded;
        let shouldExpand = window.innerWidth > mobileWidth;
        if (wasExpanded !== shouldExpand) {
            toggleContent();
        }
    });

    const toggleContent = () => {
        if (navExpanded) {
            collapseContent();
            setNavExpanded(false);
        } else {
            expandContent();
            setNavExpanded(true);
        }
    };

    const collapseContent = () => {
        setCurrentStyling({
            ...currentStyling,
            mainClass: ' collapsedMain',
            navbarIconClass: ' collapsedIcon',
            navbarIMGClass: ' collapsedIMG',
            navbarLinkClass: ' collapsedLink',
            navbarSidenavClass: ' collapsedSidenav',
            navbarTextClass: ' collapsedText',
        });
    };

    const expandContent = () => {
        if (window.innerWidth <= mobileWidth) {
            setCurrentStyling({
                ...currentStyling,
                mainClass: ' expandedMain',
                navbarContentClass: ' expandedContent',
                navbarIconClass: ' expandedIcon',
                navbarIMGClass: ' expandedIMG',
                navbarLinkClass: ' expandedLink',
                navbarSidenavClass: ' expandedSidenav',
                navbarTextClass: ' expandedText',
            });
        } else {
            setCurrentStyling({
                ...currentStyling,
                mainClass: '',
                navbarContentClass: '',
                navbarIconClass: '',
                navbarIMGClass: '',
                navbarLinkClass: '',
                navbarSidenavClass: '',
                navbarTextClass: '',
            });
        }
    };

    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = event => {
        setAnchorEl(event.currentTarget);
        setOpen(!open); // TODO: Address bug with double clicking outside when it's open. Shouldn't close and open
    };

    const handleClose = () => {
        setAnchorEl(null);
        setOpen(false);
    };

    const openModal = modalType => {
        setOpen(false);
        showModal({
            type: modalType,
            onYes: () => handleClose(),
            onNo: () => {},
        });
    };

    const handleLogout = () => {
        setOpen(false);
        Toast.success(getText('msg_b12100486176'));
        logoutSession();
        logout();
    };

    const [open, setOpen] = useState(false);
    const id = open ? 'simple-popover' : undefined;

    const getGreetingTextColor = () => {
        // Priority: styleOverride first, then publicBrand
        if (styleOverride?.pageHeaderColor2) {
            return getContrastColor(styleOverride.pageHeaderColor2);
        }
        if (styleOverride?.pageHeaderColor1) {
            return getContrastColor(styleOverride.pageHeaderColor1);
        }
        if (publicBrand?.pageHeaderColor2) {
            return getContrastColor(publicBrand.pageHeaderColor2);
        }
        if (publicBrand?.pageHeaderColor1) {
            return getContrastColor(publicBrand.pageHeaderColor1);
        }
        return ''; // Default to CSS class color
    };

    const greetingTextStyle = css`
        color: ${getGreetingTextColor()};
    `;

    const backgroundColor = () => {
        if (styleOverride?.pageHeaderColor1) {
            return css`
                background: linear-gradient(
                    to right,
                    ${styleOverride?.pageHeaderColor1},
                    ${styleOverride?.pageHeaderColor2 ||
                    styleOverride?.pageHeaderColor1}
                );
            `;
        } else if (publicBrand?.pageHeaderColor1) {
            return css`
                background: linear-gradient(
                    to right,
                    ${publicBrand?.pageHeaderColor1},
                    ${publicBrand?.pageHeaderColor2 ||
                    publicBrand?.pageHeaderColor1}
                );
            `;
        } else {
            return '';
        }
    };

    return (
        isLoggedIn && (
            <div
                className={`top ${visible ? 'headerVisible' : 'headerHidden'}`}
                css={backgroundColor()}
            >
                {user.jwt.ind == null && (
                    <div className="clientSearch">
                        <LoadingWrapper isLoading={isFavClientLoading}>
                            <Dropdown
                                allowSearch={false}
                                defaultValue={selectedClient}
                                options={favClientList}
                                onChange={updateTenant}
                                isTitleEnabled={true}
                                titleLabel={getText('msg_28e22fe3dde5')}
                                displayStarIcon={true}
                                height={'36px'}
                                isBottomOptionEnabled={
                                    !multitenantFlag ? true : false
                                }
                                bottomOptionLabel={getText('msg_8307e1ee0ea7')}
                                onBottomOptionClick={
                                    !multitenantFlag
                                        ? handleReturnToClientClick
                                        : () => {}
                                }
                            />
                        </LoadingWrapper>
                    </div>
                )}

                <div className="topInner display-flex avatar-container">
                    <div className="greeting-text" css={greetingTextStyle}>
                        {getText('msg_65611e9520e7')}{' '}
                        {user.preferred_name
                            ? user.preferred_name
                            : user.first_name}
                        !
                    </div>
                    {isMobile && !adminView ? (
                        <NavLink
                            to={ROUTES.PROFILE}
                            className="text-decoration-none"
                        >
                            <Avatar
                                className="pointer-cursor align-self-center header-avatar"
                                src={user.avatar_url ?? ''}
                            >
                                {user.first_name
                                    ? user.first_name[0] + user.last_name[0]
                                    : ''}
                            </Avatar>
                        </NavLink>
                    ) : (
                        <Avatar
                            className="pointer-cursor align-self-center header-avatar"
                            src={user.avatar_url ?? ''}
                            onClick={handleClick}
                        >
                            {user.first_name
                                ? user.first_name[0] + user.last_name[0]
                                : ''}
                        </Avatar>
                    )}
                    <Popover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gap: 0.5,
                                paddingBottom: 1,
                                paddingTop: 3.125,
                            }}
                        >
                            <Avatar
                                className="align-self-center justify-self-center header-popover-avatar"
                                src={user.avatar_url ?? ''}
                            >
                                {user.first_name
                                    ? user.first_name[0] + user.last_name[0]
                                    : ''}
                            </Avatar>
                            <div className="text-align-center pop-over-name">
                                {user.first_name + ' ' + user.last_name}
                            </div>
                            <div
                                onClick={() => {
                                    openModal(ModalTypes.PROFILE);
                                }}
                                className="pointer-cursor profile-link"
                            >
                                {getText('msg_ff4fc0276e96')}
                            </div>
                            <div
                                onClick={() => {
                                    openModal(ModalTypes.SECURITY);
                                }}
                                className="pointer-cursor profile-link"
                            >
                                {getText('msg_8be3c943b160')}
                            </div>
                            <div
                                onClick={() => {
                                    openModal(ModalTypes.COMMUNICATIONS);
                                }}
                                className="pointer-cursor profile-link"
                            >
                                {getText('msg_690476370b42')}
                            </div>
                            {ind && allowAdditionalUser && !adminView && (
                                <div
                                    onClick={() => {
                                        setOpen(false);
                                        navigate(ROUTES.ADDITIONAL_USERS);
                                    }}
                                    className="pointer-cursor profile-link"
                                >
                                    {getText('msg_d31e71815f1e')}
                                </div>
                            )}
                            {ind && (
                                <div
                                    onClick={() => {
                                        setOpen(false);
                                        navigate(ROUTES.RESOURCES, {
                                            state: {
                                                individualResourceScope: true,
                                            },
                                        });
                                    }}
                                    className="pointer-cursor profile-link"
                                >
                                    {getText('msg_05b180520b4b')}
                                </div>
                            )}
                            <Divider sx={{ backgroundColor: 'gray' }} />
                            <div
                                onClick={() => {
                                    openModal(ModalTypes.INSTALLATION);
                                }}
                                className="pointer-cursor profile-link"
                            >
                                {getText('msg_ad0bb58de6ed')}
                            </div>
                            {/* <div
                                onClick={() => {
                                    openModal(ModalTypes.FAQ);
                                }}
                                className="pointer-cursor profile-link"
                            >
                                FAQs
                            </div> */}
                            <div
                                onClick={() => {
                                    openModal(ModalTypes.PRIVACY);
                                }}
                                className="pointer-cursor profile-link"
                            >
                                {getText('msg_9db108ba6b7f')}
                            </div>
                            <div
                                onClick={() => {
                                    openModal(ModalTypes.SUPPORT);
                                }}
                                className="pointer-cursor profile-link"
                            >
                                {getText('msg_fe1a2922069b')}
                            </div>

                            <Divider sx={{ backgroundColor: 'gray' }} />
                            {renderViewSwitch()}
                            <div
                                onClick={handleLogout}
                                className="pointer-cursor profile-link"
                            >
                                {getText('msg_4e8131ca3794')}
                            </div>
                        </Box>
                    </Popover>
                </div>
            </div>
        )
    );
}

export default Header;
